import { QuestionCircleOutlined, SoundOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Collapse, Input, Spin } from 'antd';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { envApiUrl } from '../../fetchHooks/envApiUrl.ts';
import {
  type AskAIQuestionResponse,
  type AskAIQuestionResponseError,
  fetchAskAIQuestion,
} from '../../fetchHooks/useAskAI.ts';
import type { ConversationHistory, MydayType } from '../../fetchHooks/useMyday.ts';

const { TextArea } = Input;

interface InteractiveMeasurementsAnalysisProps {
  myday?: MydayType;
  onConversationUpdate?: () => void;
}

export const InteractiveMeasurementsAnalysis = ({
  myday,
  onConversationUpdate,
}: InteractiveMeasurementsAnalysisProps) => {
  const { t, i18n } = useTranslation();
  const [question, setQuestion] = useState<string>('');
  const [response, setResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [conversationHistory, setConversationHistory] = useState<ConversationHistory | null>(
    myday?.aiConversations || null,
  );
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [speakingText, setSpeakingText] = useState<string>('');
  const [isGeneratingAudio, setIsGeneratingAudio] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Update conversation history when myday prop changes
  useEffect(() => {
    setConversationHistory(myday?.aiConversations || null);
  }, [myday?.aiConversations]);

  // Cleanup: stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Convert markdown to plain text (remove markdown syntax)
  const markdownToPlainText = (markdown: string): string => {
    // Remove markdown links but keep the text
    let text = markdown.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    // Remove markdown headers
    text = text.replace(/#{1,6}\s+/g, '');
    // Remove markdown bold/italic
    text = text.replace(/\*\*([^\*]+)\*\*/g, '$1');
    text = text.replace(/\*([^\*]+)\*/g, '$1');
    // Remove markdown code blocks
    text = text.replace(/```[\s\S]*?```/g, '');
    text = text.replace(/`([^`]+)`/g, '$1');
    // Remove markdown lists
    text = text.replace(/^[\*\-\+]\s+/gm, '');
    text = text.replace(/^\d+\.\s+/gm, '');
    // Remove extra whitespace
    text = text.replace(/\n{3,}/g, '\n\n');
    text = text.trim();
    return text;
  };

  const handleSpeak = async (text: string) => {
    // Stop any ongoing speech
    if (isSpeaking && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setSpeakingText('');
      return;
    }

    // Convert markdown to plain text
    const plainText = markdownToPlainText(text);

    if (!plainText.trim()) {
      setError(t('myday.noTextToSpeak') || 'No text to speak');
      return;
    }

    setIsGeneratingAudio(true);
    setError('');

    try {
      // Get CSRF token
      const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
        withCredentials: true,
      });
      const csrfToken = csrfResponse.data.csrfToken;

      // Call backend TTS endpoint
      const response = await axios.post(
        envApiUrl + '/mydays/text-to-speech',
        {
          text: plainText,
          language: i18n.language === 'nb' ? 'no' : 'en',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
          responseType: 'blob', // Important: receive audio as blob
        },
      );

      // Create audio element and play
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setSpeakingText(text);
        setIsGeneratingAudio(false);
      };

      audio.onended = () => {
        setIsSpeaking(false);
        setSpeakingText('');
        URL.revokeObjectURL(audioUrl); // Clean up
        audioRef.current = null;
      };

      audio.onerror = (event) => {
        console.error('Audio playback error:', event);
        setIsSpeaking(false);
        setSpeakingText('');
        setIsGeneratingAudio(false);
        setError(t('myday.textToSpeechError') || 'Error playing audio');
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      await audio.play();
    } catch (err: any) {
      console.error('Error generating or playing speech:', err);
      setIsGeneratingAudio(false);

      // If OpenAI TTS is not available, fall back to Web Speech API
      if (err.response?.status === 503 && err.response?.data?.code === 'TTS_NOT_AVAILABLE') {
        console.warn('OpenAI TTS not available, falling back to Web Speech API');
        // Fallback to Web Speech API
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          const plainText = markdownToPlainText(text);
          const utterance = new SpeechSynthesisUtterance(plainText);
          utterance.lang = i18n.language === 'nb' ? 'nb-NO' : 'en-US';

          utterance.onstart = () => {
            setIsSpeaking(true);
            setSpeakingText(text);
          };

          utterance.onend = () => {
            setIsSpeaking(false);
            setSpeakingText('');
          };

          utterance.onerror = () => {
            setIsSpeaking(false);
            setSpeakingText('');
            setError(t('myday.textToSpeechError') || 'Error reading text aloud');
          };

          window.speechSynthesis.speak(utterance);
        } else {
          setError(t('myday.textToSpeechNotSupported') || 'Text-to-speech is not supported in your browser');
        }
      } else {
        setError(t('myday.textToSpeechError') || 'Error reading text aloud');
      }
    }
  };

  const handleStopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setSpeakingText('');
    }
  };

  const handleSubmit = async () => {
    if (!question.trim()) {
      setError(t('myday.questionRequired') || 'Please enter a question');
      return;
    }

    setError('');
    setResponse('');
    setIsLoading(true);

    try {
      console.log('Started askAIQuestion');
      const result = await fetchAskAIQuestion(question.trim(), myday?.id);
      const { data: generatedResponse, message, status } = result as AskAIQuestionResponse;
      console.log('Ended askAIQuestion, status: ', status, message);

      if (status === 'success') {
        setResponse(generatedResponse);
        // If mydayId was provided, the conversation was saved to the database
        // We should refresh the conversation history by calling onConversationUpdate
        if (myday?.id && onConversationUpdate) {
          // Small delay to ensure database has been updated
          setTimeout(() => {
            onConversationUpdate();
          }, 500);
        }
      } else {
        setError(t('myday.aiAnalysisError') + ' ' + (message || 'Unknown error'));
      }
    } catch (err) {
      const errorMessage = (err as AskAIQuestionResponseError)?.message || 'Unknown error';
      setError(t('myday.aiAnalysisError') + ' ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <div>
      <div className="centered-container">
        <h3>{t('myday.interactiveAnalysis') || 'Ask Questions About Your Health'}</h3>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '1rem', maxWidth: '800px' }}>
          {t('myday.interactiveAnalysisDescription') ||
            'Your question will be analyzed based on your health data. All information is anonymized.'}
        </p>

        {/* Display conversation history if it exists */}
        {conversationHistory?.conversation && conversationHistory.conversation.length > 0 && (
          <div style={{ marginBottom: '1rem', width: '100%', maxWidth: '800px' }}>
            <Collapse
              items={[
                {
                  key: '1',
                  label: t('myday.conversationHistory') || 'Conversation History',
                  children: (
                    <div>
                      {conversationHistory.conversation.map((entry, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: '1.5rem',
                            paddingBottom: '1.5rem',
                            borderBottom:
                              index < conversationHistory.conversation.length - 1 ? '1px solid #e0e0e0' : 'none',
                          }}
                        >
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong style={{ color: '#1890ff' }}>Q:</strong>{' '}
                            <span style={{ fontStyle: 'italic' }}>{entry.question}</span>
                          </div>
                          <div style={{ marginLeft: '1rem' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.5rem',
                              }}
                            >
                              <strong style={{ color: '#52c41a' }}>A:</strong>
                              <Button
                                size="small"
                                icon={
                                  isGeneratingAudio || (isSpeaking && speakingText === entry.answer) ? (
                                    <StopOutlined />
                                  ) : (
                                    <SoundOutlined />
                                  )
                                }
                                onClick={() => {
                                  if (isGeneratingAudio || (isSpeaking && speakingText === entry.answer)) {
                                    handleStopSpeaking();
                                  } else {
                                    void handleSpeak(entry.answer);
                                  }
                                }}
                                loading={isGeneratingAudio && speakingText === entry.answer}
                                type={
                                  isGeneratingAudio || (isSpeaking && speakingText === entry.answer)
                                    ? 'default'
                                    : 'text'
                                }
                              >
                                {isGeneratingAudio && speakingText === entry.answer
                                  ? t('myday.generatingAudio') || 'Generating...'
                                  : isSpeaking && speakingText === entry.answer
                                    ? t('myday.stopReading') || 'Stop Reading'
                                    : t('myday.readAloud') || 'Read Aloud'}
                              </Button>
                            </div>
                            <div
                              style={{
                                marginTop: '0.5rem',
                                padding: '0.75rem',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                              }}
                            >
                              <Markdown>{entry.answer}</Markdown>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', color: '#999', marginTop: '0.5rem' }}>
                            {new Date(entry.timestamp).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}

        <div style={{ marginBottom: '1rem', width: '100%', maxWidth: '800px' }}>
          <TextArea
            placeholder={t('myday.enterQuestion') || 'Enter your question about your measurements and health data...'}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyPress}
            rows={4}
            disabled={isLoading}
            style={{ marginBottom: '0.5rem' }}
          />
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!question.trim() || isLoading}
          >
            {t('myday.askQuestion') || 'Ask Question'}
          </Button>
        </div>

        {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

        {isLoading && (
          <div style={{ marginBottom: '1rem' }}>
            <Spin>{t('myday.waitingForAI') || 'Waiting for AI response...'}</Spin>
          </div>
        )}

        {response && !isLoading && (
          <div style={{ marginTop: '1rem', width: '100%', maxWidth: '800px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}
            >
              <h4 style={{ margin: 0 }}>{t('myday.aiResponse') || 'AI Response'}:</h4>
              <Button
                size="small"
                icon={
                  isGeneratingAudio || (isSpeaking && speakingText === response) ? <StopOutlined /> : <SoundOutlined />
                }
                onClick={() => {
                  if (isGeneratingAudio || (isSpeaking && speakingText === response)) {
                    handleStopSpeaking();
                  } else {
                    void handleSpeak(response);
                  }
                }}
                loading={isGeneratingAudio && speakingText === response}
                type={isGeneratingAudio || (isSpeaking && speakingText === response) ? 'default' : 'text'}
              >
                {isGeneratingAudio && speakingText === response
                  ? t('myday.generatingAudio') || 'Generating...'
                  : isSpeaking && speakingText === response
                    ? t('myday.stopReading') || 'Stop Reading'
                    : t('myday.readAloud') || 'Read Aloud'}
              </Button>
            </div>
            <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <Markdown>{response}</Markdown>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              <Button
                onClick={() => {
                  handleStopSpeaking();
                  setResponse('');
                  setQuestion('');
                  setError('');
                }}
              >
                {t('myday.clearResponse') || 'Clear'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
